// Bloco de pagamentos na home do aluno. Mostra em ordem de prioridade:
//   1. Pendentes (Pix em aberto pra pagar)
//   2. Próximos (disciplinas com saldo baixo → hora de comprar novo pacote)
//   3. Pagos recentes (recibo)
//
// Cada item tem ação direta — ver QR/link, comprar pacote, ver recibo.

import { useEffect, useState } from "preact/hooks";
import {
  CheckCircle2,
  ChevronRight,
  Clock,
  CreditCard,
  Receipt,
  ShoppingBag
} from "lucide-react";
import { api, type CompraRow, type StudentDisciplinaRow } from "./api";
import { showCompraPendente } from "./CompraPendenteModal";
import { openCompraPacote } from "./CompraPacoteModal";

interface Props {
  onOpenAll?: () => void;
}

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

export function PagamentosHomeCard({ onOpenAll }: Props) {
  const [pendentes, setPendentes] = useState<CompraRow[]>([]);
  const [recentesPagos, setRecentesPagos] = useState<CompraRow[]>([]);
  const [matBaixoSaldo, setMatBaixoSaldo] = useState<
    Array<{ disc: StudentDisciplinaRow; saldo: number }>
  >([]);
  const [loaded, setLoaded] = useState(false);

  async function load() {
    try {
      const [pend, paid, disc] = await Promise.all([
        api.listMyCompras("pending"),
        api.listMyCompras("paid"),
        api.listMyDisciplinas()
      ]);
      setPendentes(pend.compras);
      // Top 3 mais recentes
      setRecentesPagos(paid.compras.slice(0, 3));
      // Disciplinas com saldo <= 2 → "comprar novo pacote em breve"
      const lowBalance: Array<{ disc: StudentDisciplinaRow; saldo: number }> = [];
      await Promise.all(
        disc.disciplinas.map(async (d) => {
          if (!d.offering) return;
          try {
            const r = await api.listPacotesDisciplina(d.id, d.offering.id);
            if (r.saldo != null && r.saldo <= 2) {
              lowBalance.push({ disc: d, saldo: r.saldo });
            }
          } catch {
            /* ignore */
          }
        })
      );
      setMatBaixoSaldo(lowBalance);
    } catch {
      /* silencioso na home */
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (!loaded) return null;

  // Empty state — alinhado com os outros HomeCards (Notificações, Eventos,
  // Mensagens). Antes retornava null e deixava um buraco visível no grid
  // de 3 colunas. Agora mostra "Sem pagamentos por aqui" no mesmo padrão
  // visual dos vizinhos pra dar consistência ao tabuleiro da home.
  const isEmpty =
    pendentes.length === 0 &&
    recentesPagos.length === 0 &&
    matBaixoSaldo.length === 0;
  if (isEmpty) {
    return (
      <section class="rounded-md border border-amber-500/40 bg-amber-500/5 p-4">
        <header class="mb-2 flex items-center justify-between gap-2">
          <h2 class="flex items-center gap-2 text-sm font-bold text-amber-700 dark:text-amber-400">
            <CreditCard className="size-4" />
            Pagamentos
          </h2>
        </header>
        <p class="rounded-md border border-dashed border-amber-500/30 bg-card/40 px-3 py-3 text-center text-xs text-muted-foreground">
          Sem pagamentos no momento.
        </p>
      </section>
    );
  }

  return (
    <section class="rounded-md border border-amber-500/40 bg-amber-500/5 p-4">
      <header class="mb-3 flex items-center justify-between gap-2">
        <h2 class="flex items-center gap-2 text-sm font-bold text-amber-700 dark:text-amber-400">
          <CreditCard className="size-4" />
          Pagamentos
        </h2>
        {onOpenAll && (
          <button
            type="button"
            onClick={onOpenAll}
            class="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:underline dark:text-amber-400"
          >
            Ver todos <ChevronRight className="size-3.5" />
          </button>
        )}
      </header>

      <div class="space-y-3">
        {/* Pendentes */}
        {pendentes.length > 0 && (
          <div>
            <h3 class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
              <Clock className="size-3" />
              Pendentes ({pendentes.length})
            </h3>
            <ul class="space-y-1">
              {pendentes.map((c) => (
                <li
                  key={c.id}
                  class="flex items-center gap-2 rounded-md border border-amber-500/30 bg-background/60 px-3 py-2"
                >
                  <div class="min-w-0 flex-1">
                    <div class="truncate text-xs font-bold">
                      {c.pacoteNomeSnap}
                    </div>
                    <div class="truncate text-[11px] text-muted-foreground">
                      {c.numAulasSnap} aulas · {formatBRL(c.valorCents)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => showCompraPendente(c)}
                    class="inline-flex items-center gap-1 rounded-md border border-amber-500/50 bg-amber-500/15 px-2.5 py-1 text-[11px] font-bold text-amber-700 hover:bg-amber-500/25 dark:text-amber-400"
                  >
                    Pagar
                    <ChevronRight className="size-3" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Próximos (saldo baixo) */}
        {matBaixoSaldo.length > 0 && (
          <div>
            <h3 class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-primary">
              <ShoppingBag className="size-3" />
              Comprar novo pacote
            </h3>
            <ul class="space-y-1">
              {matBaixoSaldo.map(({ disc, saldo }) => (
                <li
                  key={disc.id}
                  class="flex items-center gap-2 rounded-md border border-primary/30 bg-background/60 px-3 py-2"
                >
                  <div class="min-w-0 flex-1">
                    <div class="truncate text-xs font-bold">{disc.name}</div>
                    <div class="truncate text-[11px] text-muted-foreground">
                      {saldo === 0
                        ? "Sem créditos — renovar"
                        : `Resta${saldo === 1 ? "" : "m"} ${saldo} aula${saldo === 1 ? "" : "s"}`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      openCompraPacote({
                        disciplinaId: disc.id,
                        disciplinaName: disc.name,
                        offeringId: disc.offering!.id
                      })
                    }
                    class="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-[11px] font-bold text-primary-foreground hover:bg-primary/90"
                  >
                    Comprar
                    <ShoppingBag className="size-3" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pagos recentes */}
        {recentesPagos.length > 0 && (
          <div>
            <h3 class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
              <CheckCircle2 className="size-3" />
              Pagos recentes
            </h3>
            <ul class="space-y-1">
              {recentesPagos.map((c) => (
                <li
                  key={c.id}
                  class="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-background/60 px-3 py-2"
                >
                  <div class="min-w-0 flex-1">
                    <div class="truncate text-xs font-bold">
                      {c.pacoteNomeSnap}
                    </div>
                    <div class="truncate text-[11px] text-muted-foreground">
                      {formatBRL(c.valorCents)}
                      {c.paidAt
                        ? ` · ${new Date(c.paidAt).toLocaleDateString("pt-BR")}`
                        : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => showCompraPendente(c)}
                    title="Ver recibo"
                    aria-label="Ver recibo"
                    class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-accent"
                  >
                    <Receipt className="size-3" />
                    Recibo
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
